import { API } from '@aws-amplify/api'
import { useQuery } from 'react-query'
import * as Sentry from '@sentry/nextjs'

export type PhotoResolutions = {
  high: string
  low: string
}

export type HoaValueSource = 'mls' | 'estimate' | 'none' | 'unknown'

/**
 * Mirrors backend/src/services/listing/domain/PaymentBlocks.ts. `total`
 * excludes HOA; `total_with_hoa` adds it when `hoa_monthly` is non-null.
 */
export type PaymentV3Product = {
  base: number
  total: number
  total_with_hoa: number
}

export type PaymentV3 = {
  tax_monthly: number
  insurance_monthly: number
  hoa_monthly: number | null
  products: {
    saver: PaymentV3Product
    boost: PaymentV3Product
  }
}

export type Listing = {
  address: string
  baths: number
  beds: number
  existing_offer: boolean
  id: number
  live: boolean
  list_price: number
  sqft: number
  home_description: string
  list_date: string
  acre_sold: { date: string } | null
  geolocation: {
    latitude: number
    longitude: number
  } | null
  thumbnail_resolutions: PhotoResolutions | null
  estimatedSalePrice: number
  paymentOptions: {
    ownershipMin: {
      percent: number
      amount: number
      acreMonthly: number
    }
    ownershipMax: {
      percent: number
      amount: number
      acreMonthly: number
    }
    saver: {
      percent: number
      amount: number
      acreMonthly: number
    }
    boost: {
      percent: number
      amount: number
      acreMonthly: number
    }
  }
  taxes: {
    annualAmount: number
  }
  interestRate?: number
  property_status?: string
  featured_at_page?: number
  market: string
  /**
   * mls_feeds.feed_id — optional so stale cached payloads don't break.
   * SEARCH payloads only: detail payloads never set this top-level field;
   * they carry the same identity as `mlsIdx.feedName` (see getListingById.ts
   * — IndividualListing extends this type). Reaching for
   * `listingDetails.feedName` on a detail page type-checks but is always
   * undefined; use `mlsIdx.feedName` there.
   */
  feedName?: string
  /**
   * v3 payment contract from PDE-56. `payment_v3` is omitted entirely — never
   * null — whenever the backend's v3 gate did not pass, so the optional
   * property IS the signal. Read it through `services/flags/selectPayment`
   * rather than directly.
   */
  hoa_monthly?: number | null
  hoa_value_source?: HoaValueSource | null
  payment_v3?: PaymentV3
}

export const getListings = async (market: string): Promise<Listing[]> => {
  return API.get('listing', '/search', {
    queryStringParameters: {
      market,
    },
  }).catch(captureExceptionAndThrow)
}

export const useListings = (market: string, initialData?: Array<Listing>) => {
  return useQuery(['listings', market], () => getListings(market), {
    initialData,
    refetchOnMount: 'always',
  })
}

function captureExceptionAndThrow(error: unknown) {
  Sentry.captureException(error)

  throw error
}
